import { getLogger } from "@utils/ermineLoggerUtils";
import {
    getDeviceBrowser,
    getDeviceModel,
    getDeviceOSName,
    getDeviceTouchInfo,
    getDeviceType,
    isInternalTestTraffic,
} from "@utils/userAgentUtils";
import {
    fetchStreamingURLCache,
    refreshStreamingURLCache,
} from "@utils/StreamingURLCache";
import { useSessionLaunchMetricStore } from "@hooks/useSessionLaunchMetricStore";
import { useCreateRemoteBrowser } from "@hooks/api/useCreateRemoteBrowser";
import { useCallback } from "react";
import { CookieList } from "@customTypes/cookies";
import { getPortalConfig } from "@utils/configurationUtils";
import { MetricName } from "@constants/logging";
import { useGetLogger } from "@hooks/useGetLogger";

const publishDeviceMetrics = () => {
    const log = getLogger();
    log.publishMetricWithDimension(
        MetricName.DEVICE_BROWSER,
        getDeviceBrowser()
    );
    log.publishMetricWithDimension(MetricName.DEVICE_TYPE, getDeviceType());
    log.publishMetricWithDimension(MetricName.DEVICE_MODEL, getDeviceModel());
    log.publishMetricWithDimension(MetricName.DEVICE_OS, getDeviceOSName());
    log.publishMetricWithDimension(
        MetricName.DEVICE_TOUCHSCREEN,
        getDeviceTouchInfo()
    );
};

// TODO [LOWA-30534] new step based flow will use `src/stepFlowHooks/useGetStreamingUrl.ts` instead
export const useGetStreamingUrl = () => {
    const log = useGetLogger();

    const setCachedSessionEndpointLoadedTimestampIfEmpty =
        useSessionLaunchMetricStore(
            (store) => store.setCachedSessionEndpointLoadedTimestampIfEmpty
        );
    const setSessionEndpointCreatedTimestampIfEmpty =
        useSessionLaunchMetricStore(
            (store) => store.setSessionEndpointCreatedTimestampIfEmpty
        );
    const createRemoteBrowser = useCreateRemoteBrowser();

    return useCallback(
        async (cookies: CookieList) => {
            const cachedStreamingURL = fetchStreamingURLCache();

            if (cachedStreamingURL !== undefined) {
                const { streamingURL } = cachedStreamingURL;
                log.publishCounterMetric(MetricName.STREAMING_URL_CACHE_HIT);
                setCachedSessionEndpointLoadedTimestampIfEmpty();

                return streamingURL;
            }

            const portalConfig = getPortalConfig();
            const data = await createRemoteBrowser({
                portalArn: portalConfig.portalArn,
                cookies: cookies,
            });

            try {
                log.publishHttpStatusMetric(
                    MetricName.CREATE_REMOTE_BROWSER,
                    200
                );
                setSessionEndpointCreatedTimestampIfEmpty();

                if (!isInternalTestTraffic()) {
                    publishDeviceMetrics();
                }

                // Refill cache and log warning in case it failed
                const isCacheWriteSuccess = refreshStreamingURLCache(
                    data.streamingUrl
                );
                if (isCacheWriteSuccess === false) {
                    await log.warn(
                        "StreamingURL session cache write back failed."
                    );
                }

                return data.streamingUrl;
            } catch (err) {
                await log.error("Failed to consume fresh streaming URL", err);
                throw err;
            }
        },
        [
            setCachedSessionEndpointLoadedTimestampIfEmpty,
            setSessionEndpointCreatedTimestampIfEmpty,
            createRemoteBrowser,
        ]
    );
};
